import { Fragment, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTitle,
  Table,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { isToday } from 'date-fns';
import type {
  PolicyMatchMode,
  PolicyResponse,
  PolicyRuleGroup,
} from '@http/queries/useCreatePolicy';
import { getRuleFieldLabel, getRuleOperatorLabel } from './PolicyRuleLabels';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { getPriorityTranslationKey } from '../priority';
import type { PolicyDomainOption } from './usePolicyDomainOptions';
import { getNextAuditSchedule } from '@/helpers/auditSchedule';
import { T } from '@/i18n/T';

function MatchModeConnector({ matchMode }: { matchMode: PolicyMatchMode }) {
  const { t } = useInlineTranslation();
  return (
    <div className='eye:flex eye:items-center eye:gap-xs'>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
      <Typography variant='body-s' className='eye:text-grey-120'>
        {matchMode === 'all' ? t('and') : t('or')}
      </Typography>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
    </div>
  );
}

const scopeNounKeys: Record<string, string> = {
  image: 'nounImage',
  'meta-tag': 'nounMetaTag',
  file: 'nounFile',
};

/** "Any image where…" header for scoped groups; page groups need none. */
function RuleGroupBlock({ group }: { group: PolicyRuleGroup }) {
  const { t } = useInlineTranslation();
  const heading =
    group.scope === 'page'
      ? null
      : t(group.quantifier === 'every' ? 'everyScopeWhere' : 'anyScopeWhere', {
          scope: t(scopeNounKeys[group.scope] ?? group.scope),
        });
  return (
    <div className='eye:flex eye:flex-col eye:gap-xs eye:rounded-8 eye:border eye:border-grey-20 eye:p-xs'>
      {heading ? (
        <Typography variant='body-s' className='eye:text-grey-120'>
          {heading}
        </Typography>
      ) : null}
      {group.rules.map((rule, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <MatchModeConnector matchMode={group.matchMode} />
          ) : null}
          <div className='eye:flex eye:flex-wrap eye:items-baseline eye:gap-4xs'>
            <Typography variant='body-s'>
              {t(getRuleFieldLabel(rule.field))}
            </Typography>
            <Typography className='eye:text-grey-120'>
              {t(getRuleOperatorLabel(rule.operator)).toLowerCase()}
            </Typography>
            <Typography className='eye:break-all'>
              &ldquo;{rule.value}&rdquo;
            </Typography>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

type IncludedDomain = {
  id: number;
  domain?: PolicyDomainOption;
};

function IncludedDomainsTable({ domains }: { domains: IncludedDomain[] }) {
  const { t } = useInlineTranslation();
  const { formatDate } = useLocaleFormat();
  const columns = useMemo<ColumnDef<IncludedDomain>[]>(
    () => [
      {
        id: 'domain',
        header: t('domain'),
        accessorFn: ({ id, domain }) => domain?.label ?? id,
        enableSorting: false,
        cell: ({ row }) => row.original.domain?.label ?? row.original.id,
      },
      {
        id: 'lastAudit',
        header: t('lastAudit'),
        accessorFn: ({ domain }) => domain?.lastCompletedAudit,
        enableSorting: false,
        cell: ({ row }) => {
          const lastCompletedAudit = row.original.domain?.lastCompletedAudit;

          return lastCompletedAudit ? (
            <time dateTime={lastCompletedAudit}>
              {formatDate(new Date(lastCompletedAudit), 'P')}
            </time>
          ) : (
            '—'
          );
        },
      },
      {
        id: 'nextAudit',
        header: t('nextAudit'),
        accessorFn: ({ domain }) =>
          getNextAuditSchedule(domain?.auditEntry).nextAudit?.toISOString(),
        enableSorting: false,
        cell: ({ row }) => {
          const auditEntry = row.original.domain?.auditEntry;
          const nextAudit = getNextAuditSchedule(auditEntry).nextAudit;

          return nextAudit ? (
            <time dateTime={nextAudit.toISOString()}>
              {isToday(nextAudit)
                ? t('dateIsToday')
                : formatDate(nextAudit, 'P')}
              {auditEntry?.allowedTime && auditEntry.allowedTime !== '00:00:00'
                ? ` ${t('after')} ${auditEntry.allowedTime.slice(0, -3)}`
                : null}
            </time>
          ) : (
            '—'
          );
        },
      },
    ],
    [formatDate, t],
  );

  return (
    <Table
      id='policy-included-domains'
      columns={columns}
      data={domains}
      disableExport
      hideFooter={domains.length <= 50}
      paginationLabel={t('includedDomains')}
    >
      <Typography variant='body-s-highlight' className='eye:mb-5xs'>
        <T k='includedDomains' />
      </Typography>
    </Table>
  );
}

export function PolicyRuleDialog({
  policy,
  onOpenChange,
  domainOptions,
}: {
  policy: PolicyResponse | null;
  onOpenChange: (open: boolean) => void;
  domainOptions: PolicyDomainOption[];
}) {
  const { t } = useInlineTranslation();
  const includedDomains = useMemo(
    () =>
      (policy?.auditEntryIds ?? []).map((id) => ({
        id,
        domain: domainOptions.find((domain) => domain.id === id),
      })),
    [domainOptions, policy?.auditEntryIds],
  );

  return (
    <Dialog open={policy !== null} onOpenChange={onOpenChange}>
      <DialogContent maxWidth='md' className='eye:w-full'>
        <DialogTitle>
          <T k='policyDetails' />
        </DialogTitle>
        {policy ? (
          <>
            <div className='eye:flex eye:flex-col eye:gap-s'>
              <div className='eye:grid eye:gap-xs eye:sm:grid-cols-2'>
                <DetailField label={t('policyName')} value={policy.name} />
                <DetailField
                  label={t('priority')}
                  value={t(getPriorityTranslationKey(policy.priority))}
                />
                {policy.description ? (
                  <DetailField
                    label={t('description')}
                    value={policy.description}
                    className='eye:sm:col-span-2'
                  />
                ) : null}
                <DetailField
                  label={t('domainScope')}
                  value={
                    policy.domainScope === 'all-domains'
                      ? t('allDomainsInAccount')
                      : t('specificDomainsOnly')
                  }
                />
              </div>

              {policy.domainScope === 'selected-domains' ? (
                <IncludedDomainsTable domains={includedDomains} />
              ) : null}

              <div className='eye:flex eye:flex-col eye:gap-xs'>
                <Typography variant='body-s-highlight'>
                  <T k='issueRule' />
                </Typography>
                {policy.rule.groups.map((group, index) => (
                  <Fragment key={index}>
                    {index > 0 ? (
                      <MatchModeConnector matchMode={policy.rule.matchMode} />
                    ) : null}
                    <RuleGroupBlock group={group} />
                  </Fragment>
                ))}
              </div>
            </div>
          </>
        ) : null}
        <DialogActions>
          <DialogClose asChild>
            <Button type='button' variant='outline'>
              {t('close')}
            </Button>
          </DialogClose>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`eye:flex eye:flex-col eye:gap-5xs ${className ?? ''}`}>
      <Typography variant='body-s' className='eye:text-grey-120'>
        {label}
      </Typography>
      <Typography className='eye:break-words'>{value}</Typography>
    </div>
  );
}

export default PolicyRuleDialog;
